import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { defineMessages, injectIntl, intlShape } from 'react-intl';

import log from '../lib/log';
import sharedMessages from '../lib/shared-messages';

import {
    LoadingStates,
    getIsLoadingUpload,
    getIsShowingWithoutId,
    onLoadedProject,
    requestProjectUpload,
    getIsLoadingSentencesUpload,
    onReloadProject,
    requestSentenceUpload
} from '../reducers/project-state';

import {
    setProjectPreTitle,
    setProjectNew
} from '../reducers/project-title';

import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';
import {
    closeFileMenu
} from '../reducers/menus';
import {
    questionType,
    showQuestionDialog,
    hideStartAppBrainTypePicker
} from '../reducers/dialog'
import {
    updatePickedBrainType,
    isEnableEntry,
    isEnableEduAndEntry,
    isEnableWebVR,
} from '../reducers/picked-brain-type'
import { BRAIN_TYPE } from '../lib/brains.js';
import { matchExtension } from '../lib/file-uploader';
import {
    getWorkspace
} from '../reducers/block';

/**
 * SBFileUploader component passes a file input, load handler and props to its child.
 * It expects this child to be a function with the signature
 *     function (renderFileInput, handleLoadProject) {}
 * The component can then be used to attach project loading functionality
 * to any other component:
 *
 * <SBFileUploader>{(className, renderFileInput, handleLoadProject) => (
 *     <MyCoolComponent
 *         className={className}
 *         onClick={handleLoadProject}
 *     >
 *         {renderFileInput()}
 *     </MyCoolComponent>
 * )}</SBFileUploader>
 */

const messages = defineMessages({
    loadError: {
        id: 'gui.projectLoader.loadError',
        defaultMessage: 'The project file that was selected failed to load.',
        description: 'An error that displays when a local project file fails to load.'
    }
});

const LOAD_ALL_UPLOAD = 'LOAD_ALL_UPLOAD';
const LOAD_SENTENCES_UPLOAD = 'LOAD_SENTENCES_UPLOAD';

class SBFileUploader extends React.Component {
    constructor(props) {
        super(props);
        bindAll(this, [
            'renderFileInput',
            'setFileInput',
            'handleChange',
            'handleClick',
            'onload',
            'resetFileInput',
            'loadData',
            'loadFinishedData'
        ]);
    }

    UNSAFE_componentWillMount() {
        log.info('sb-file-uploader UNSAFE_componentWillMount')
        this.reader = new FileReader();
        this.reader.onload = this.onload;
        this.resetFileInput();
    }

    componentWillUnmount() {
        log.info('sb-file-uploader componentWillUnmount')
    }

    componentDidUpdate(prevProps) {
        if (this.props.isLoadingUpload && !prevProps.isLoadingUpload && this.fileToUpload && this.reader) {
            this.part = LOAD_ALL_UPLOAD;
            this.reader.readAsArrayBuffer(this.fileToUpload);
            log.info('sb-file-uploader isLoadingUpload:', this.props.isLoadingUpload)
        }
        if (this.props.isLoadingSentencesUpload && !prevProps.isLoadingSentencesUpload && this.fileToUpload && this.reader) {
            this.part = LOAD_SENTENCES_UPLOAD;
            this.reader.readAsArrayBuffer(this.fileToUpload);
            log.info('sb-file-uploader isLoadingSentencesUpload:', this.props.isLoadingSentencesUpload)
        }

        // if (this.props.onMenuBarClickSave) {
        //     console.log('sb-file-uploader onMenuBarClickSave')
        //     this.props.onMenuBarClickSave();
        // }
    }

    resetFileInput() {
        this.fileToUpload = null;
        if (this.fileInput) {
            this.fileInput.value = null;
        }
    }
    // called when user has finished selecting a file to upload
    handleChange(e) {
        const {
            intl,
            isShowingWithoutId,
            loadingState,
            projectChanged,
            userOwnsProject,
            importSentences
        } = this.props;

        const thisFileInput = e.target;
        if (thisFileInput.files) { // Don't attempt to load if no file was selected
            this.fileToUpload = thisFileInput.files[0];

            if (importSentences) {
                this.props.requestSentenceUpload();
            } else {
                console.log('sb-file-uploader handleChange loadingState: ', loadingState)
                this.props.requestProjectUpload(loadingState);
            }
        }
    }
    // called when file upload raw data is available in the reader
    onload() {
        if (this.reader) {
            this.props.onLoadingStarted();
            this.loadData(this.reader.result)
                .then(() => {
                    this.loadFinishedData();
                    this.resetFileInput();
                })
                .catch(error => {
                    log.warn(error);
                    alert(this.props.intl.formatMessage(messages.loadError)); // eslint-disable-line no-alert
                    this.props.onLoadingFinished(this.props.loadingState, false);
                    // Reset the file input after project is loaded
                    // This is necessary in case the user wants to reload a project
                    this.resetFileInput();
                });
        }
    }
    loadData(input) {
        if (this.part == LOAD_SENTENCES_UPLOAD) {
            return this.props.vm.loadSentences(input);
        }
        return this.props.vm.loadProject(input);
    }
    loadFinishedData() {
        if (this.part == LOAD_SENTENCES_UPLOAD) {
            this.props.onLoadingSentencesFinished(this.props.loadingState, true);
        } else {
            const filename = this.fileToUpload && this.fileToUpload.name;
            if (matchExtension(filename, this.props.vm.getBrainType(), this.props.isEnableWebVR)) {
                this.props.onLoadingFinished(this.props.loadingState, true);

                // Reset the file input after project is loaded
                // This is necessary in case the user wants to reload a project
                this.props.onUpdateProjectTitle(filename.split('.').slice(0, -1).join('.'));
                this.props.setProjectPreTitle(filename.split('.').slice(0, -1).join('.'));
                this.props.setProjectNew(false);
            } else {
                this.props.onShowQuestionExtensionErrDialog();
                this.props.onReloadProject();
            }
        }

        if (this.props.onClose) {
            this.props.onClose();
        }
    }
    handleClick() {
        // open filesystem browsing window
        this.fileInput.click();
    }
    setFileInput(input) {
        this.fileInput = input;
    }
    renderFileInput() {
        let brainFileType = '.abot';
        if (this.props.isEnableEntry) {
            brainFileType += ',.bbot'
        }
        if (this.props.isEnableEduAndEntry) {
            brainFileType += ',.cbot'
        }
        return (
            <input
                accept={this.props.vm.getBrainType() == BRAIN_TYPE.WEB_VR ? '.vr' : brainFileType}
                ref={this.setFileInput}
                style={{ display: 'none' }}
                type="file"
                onChange={this.handleChange}
            />
        );
    }
    render() {
        return this.props.children(this.props.className, this.renderFileInput, this.handleClick);
    }
}

SBFileUploader.propTypes = {
    canSave: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    children: PropTypes.func,
    className: PropTypes.string,
    closeFileMenu: PropTypes.func,
    intl: intlShape.isRequired,
    isLoadingUpload: PropTypes.bool,
    isShowingWithoutId: PropTypes.bool,
    loadingState: PropTypes.oneOf(LoadingStates),
    onLoadingFinished: PropTypes.func,
    onReloadProject: PropTypes.func,
    onLoadingStarted: PropTypes.func,
    onUpdateProjectTitle: PropTypes.func,
    setProjectPreTitle: PropTypes.func,
    projectChanged: PropTypes.bool,
    requestProjectUpload: PropTypes.func,
    requestSentenceUpload: PropTypes.func,
    userOwnsProject: PropTypes.bool,
    importSentences: PropTypes.bool,
    vm: PropTypes.shape({
        loadProject: PropTypes.func,
        getProjectName: PropTypes.func
    }),
    getWorkspace: PropTypes.object,
    setProjectNew: PropTypes.func,
    onClose: PropTypes.func
};
SBFileUploader.defaultProps = {
    className: ''
};
const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        isLoadingUpload: getIsLoadingUpload(loadingState),
        isLoadingSentencesUpload: getIsLoadingSentencesUpload(loadingState),
        isShowingWithoutId: getIsShowingWithoutId(loadingState),
        loadingState: loadingState,
        projectChanged: state.scratchGui.projectChanged,
        vm: state.scratchGui.vm,
        isEnableWebVR: isEnableWebVR(state),
        isEnableEntry: isEnableEntry(state),
        isEnableEduAndEntry: isEnableEduAndEntry(state),
        getWorkspace: getWorkspace(state)
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeFileMenu: () => dispatch(closeFileMenu()),
    onLoadingFinished: (loadingState, success) => {
        dispatch(onLoadedProject(loadingState, ownProps.canSave, success));
        dispatch(closeLoadingProject());
        dispatch(closeFileMenu());
        dispatch(hideStartAppBrainTypePicker());
        dispatch(updatePickedBrainType());
    },
    onLoadingSentencesFinished: (loadingState, success) => {
        dispatch(onLoadedProject(loadingState, ownProps.canSave, success));
        dispatch(closeLoadingProject());
        dispatch(closeFileMenu());
    },
    onReloadProject: () => dispatch(onReloadProject()),
    requestProjectUpload: loadingState => dispatch(requestProjectUpload(loadingState)),
    requestSentenceUpload: () => dispatch(requestSentenceUpload()),
    onLoadingStarted: () => dispatch(openLoadingProject()),
    setProjectPreTitle: title => dispatch(setProjectPreTitle(title)),
    onShowQuestionExtensionErrDialog: () => dispatch(showQuestionDialog(questionType.QUESTION_FILE_EXTENSION_NOT_MATCH)),
    setProjectNew: isNew => dispatch(setProjectNew(isNew)),
});

// Allow incoming props to override redux-provided props. Used to mock in tests.
const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
    {}, stateProps, dispatchProps, ownProps
);

export default connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(injectIntl(SBFileUploader));
